import { send } from '@actual-app/core/platform/client/connection';
import type { AtLeastOne } from '@actual-app/core/types/util';
import { createAction, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import { pushModal } from '#modals/modalsSlice';
import { loadPrefs } from '#prefs/prefsSlice';
import { createAppAsyncThunk } from '#redux';
import { getUploadError } from '#util/error';

const sliceName = 'app';

type AppState = {
  loadingText: string | null;
  managerHasInitialized: boolean;
  versionInfo: {
    latestVersion: string;
    isOutdated: boolean;
  } | null;
};

const initialState: AppState = {
  loadingText: null,
  managerHasInitialized: false,
  versionInfo: null,
};

export const resetApp = createAction(`${sliceName}/resetApp`);

export const resetSync = createAppAsyncThunk(
  `${sliceName}/resetSync`,
  async (_, { dispatch }) => {
    const { error } = await send('sync-reset');

    if (error) {
      alert(getUploadError(error));

      if (
        (error.reason === 'encrypt-failure' &&
          (error.meta as { isMissingKey?: boolean }).isMissingKey) ||
        error.reason === 'file-has-new-key'
      ) {
        dispatch(
          pushModal({
            modal: {
              name: 'fix-encryption-key',
              options: {
                onSuccess: () => {
                  // TODO: There won't be a loading indicator for this
                  void dispatch(resetSync());
                },
              },
            },
          }),
        );
      } else if (error.reason === 'encrypt-failure') {
        dispatch(
          pushModal({
            modal: {
              name: 'create-encryption-key',
              options: { recreate: true },
            },
          }),
        );
      }
    } else {
      await dispatch(sync());
    }
  },
);

export const sync = createAppAsyncThunk(
  `${sliceName}/sync`,
  async (_, { dispatch, getState }) => {
    const prefs = getState().prefs.local;
    if (prefs && prefs.id) {
      const result = await send('sync');
      if (result && 'error' in result) {
        return { error: result.error };
      }

      // Update the prefs
      await dispatch(loadPrefs());
    }

    return {};
  },
);

// Workaround for partial types in actions.
// https://github.com/reduxjs/redux-toolkit/issues/1423#issuecomment-902680573
type SetAppStatePayload = AtLeastOne<AppState>;

const appSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    setAppState(state, action: PayloadAction<SetAppStatePayload>) {
      return {
        ...state,
        ...action.payload,
      };
    },
  },
  extraReducers: builder => {
    builder.addCase(resetApp, state => ({
      ...initialState,
      loadingText: state.loadingText || null,
      managerHasInitialized: state.managerHasInitialized || false,
    }));
  },
});

export const { name, reducer, getInitialState } = appSlice;

export const actions = {
  ...appSlice.actions,
  resetApp,
  resetSync,
  sync,
};

export const { setAppState } = actions;
